// src/pages/student/MyEventsPage.js
import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Button,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import {
    CalendarToday as CalendarIcon,
    LocationOn as LocationIcon,
    CheckCircle as CheckIcon,
    Download as DownloadIcon,
    Cancel as CancelIcon,
    Pending as PendingIcon,
} from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import { getUserEvents, cancelRegistration } from "../../services/eventService";
import AdminLayout from "../AdminLayout";

export default function MyEventsPage() {
    const { userProfile } = useAuth();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [debugInfo, setDebugInfo] = useState(null);

    useEffect(() => {
        fetchMyEvents();
    }, [userProfile]);

    const fetchMyEvents = async () => {
        try {
            setLoading(true);
            setError("");

            if (!userProfile) {
                setError("User profile not loaded. Please refresh the page.");
                setDebugInfo({ error: "No userProfile" });
                return;
            }



            // Try multiple identifiers
            const identifiers = [
                userProfile.studentId,
                userProfile.uid,
                userProfile.email
            ].filter(Boolean);

            // console.log("🔑 Will try identifiers:", identifiers);

            let data = [];

            // Try each identifier until we find events
            for (const identifier of identifiers) {
                // console.log(`🔍 Trying identifier: ${identifier}`);
                const result = await getUserEvents(identifier);
                if (result.length > 0) {
                    // console.log(`✅ Found ${result.length} events with identifier: ${identifier}`);
                    data = result;
                    break;
                }
            }

            // console.log("📊 Final events found:", data.length);
            setEvents(data);

            // setDebugInfo({
            //     userProfile: {
            //         uid: userProfile.uid,
            //         email: userProfile.email,
            //         studentId: userProfile.studentId,
            //     },
            //     eventsFound: data.length,
            //     identifiersTried: identifiers
            // });

            if (data.length === 0) {
                // setError("No events found. This might mean: (1) You haven't registered for any events yet, or (2) The studentId in your registration doesn't match your profile. Check the browser console for debug info.");
            }
        } catch (err) {
            console.error("❌ Error fetching my events:", err);
            setError(`Failed to fetch your events: ${err.message}`);
            setDebugInfo({ error: err.message });
        } finally {
            setLoading(false);
        }
    };

    const handleCancelRegistration = async (eventId) => {
        if (!window.confirm("Are you sure you want to cancel your registration?")) {
            return;
        }

        try {
            const studentId = userProfile.studentId || userProfile.uid;
            await cancelRegistration(eventId, studentId);
            setSuccess("Registration cancelled successfully");
            fetchMyEvents();
        } catch (err) {
            console.error("Error canceling registration:", err);
            setError("Failed to cancel registration");
        }
    };

    const getCertificateStatus = (event) => {
        const reg = event.userRegistration;

        if (reg?.certificateUrl) {
            return (
                <Button
                    size="small"
                    variant="contained"
                    href={reg.certificateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<DownloadIcon />}
                    sx={{
                        backgroundColor: "#28a745",
                        "&:hover": { backgroundColor: "#218838" },
                    }}
                >
                    Download
                </Button>
            );
        }

        if (reg?.attended) {
            return (
                <Chip
                    icon={<PendingIcon />}
                    label="Certificate Pending"
                    color="warning"
                    size="small"
                />
            );
        }

        return (
            <Chip
                label="Not Available"
                color="default"
                size="small"
            />
        );
    };

    if (loading) {
        return (
            <AdminLayout title="My Events">
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading your events...</Typography>
                </Box>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="My Events">
            <Box>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "#003366", mb: 3 }}
                >
                    📅 My Registered Events
                </Typography>

                {error && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" onClose={() => setSuccess("")} sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {/* Debug Info Card - Remove this after fixing */}
                {debugInfo && (
                    <Card sx={{ mb: 2, backgroundColor: "#f0f0f0" }}>
                        <CardContent>
                            <Typography variant="caption" component="pre" sx={{ fontSize: 10 }}>
                                {JSON.stringify(debugInfo, null, 2)}
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                {/* Summary Cards */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #003366 0%, #004d99 100%)",
                                color: "#fff",
                            }}
                        >
                            <CardContent>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                    {events.length}
                                </Typography>
                                <Typography variant="body2">Total Events</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                                color: "#fff",
                            }}
                        >
                            <CardContent>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                    {events.filter((e) => e.userRegistration?.attended).length}
                                </Typography>
                                <Typography variant="body2">Attended</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #C1272D 0%, #e63946 100%)",
                                color: "#fff",
                            }}
                        >
                            <CardContent>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                    {events.filter((e) => e.userRegistration?.certificateUrl).length}
                                </Typography>
                                <Typography variant="body2">Certificates Issued</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Events List */}
                {events.length === 0 ? (
                    <Card>
                        <CardContent>
                            <Typography align="center" color="textSecondary" sx={{ py: 4 }}>
                                You have not registered for any events yet.
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                                <Button
                                    variant="contained"
                                    href="/student/events"
                                    sx={{
                                        backgroundColor: "#C1272D",
                                        "&:hover": { backgroundColor: "#a01f25" },
                                    }}
                                >
                                    Browse Events
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Event Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Venue</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Attendance</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Certificate</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {events.map((event) => {
                                    const reg = event.userRegistration;
                                    const eventStatus = event.eventStatus || "Upcoming";

                                    return (
                                        <TableRow key={event.id} hover>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 600, color: "#003366" }}
                                                >
                                                    {event.title}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <CalendarIcon sx={{ fontSize: 16, color: "#666" }} />
                                                    <Typography variant="body2">
                                                        {event.eventDate
                                                            ? new Date(event.eventDate).toLocaleDateString()
                                                            : "TBA"}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <LocationIcon sx={{ fontSize: 16, color: "#666" }} />
                                                    <Typography variant="body2">
                                                        {event.venue || "TBA"}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={event.eventType || "Event"}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {eventStatus === "Upcoming" && (
                                                    <Chip label="Upcoming" color="info" size="small" />
                                                )}
                                                {eventStatus === "Ongoing" && (
                                                    <Chip label="Ongoing" color="success" size="small" />
                                                )}
                                                {eventStatus === "Completed" && (
                                                    <Chip label="Completed" color="primary" size="small" />
                                                )}
                                                {eventStatus === "Closed" && (
                                                    <Chip label="Closed" color="default" size="small" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {reg?.attended ? (
                                                    <Chip
                                                        icon={<CheckIcon />}
                                                        label="Attended"
                                                        color="success"
                                                        size="small"
                                                    />
                                                ) : (
                                                    <Chip
                                                        icon={<CancelIcon />}
                                                        label="Not Marked"
                                                        color="default"
                                                        size="small"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>{getCertificateStatus(event)}</TableCell>
                                            <TableCell>
                                                {!reg?.attended && eventStatus !== "Closed" && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => handleCancelRegistration(event.id)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </AdminLayout>
    );
}
